class TabSelector extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.tab-selector__track');
    this.indicator = this.querySelector('.tab-selector__indicator');
    this.tabs = [...this.querySelectorAll('.tab-selector__tab')];
    if (!this.track || !this.indicator || this.tabs.length === 0) return;

    this.activeIndex = Math.max(0, this.tabs.findIndex(t => t.classList.contains('tab-selector__tab--active')));
    this.isDragging = false;
    this.dragStartX = 0;
    this.indicatorStartX = 0;

    this.positionIndicator(this.activeIndex, false);

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        if (!this.isDragging) this.selectTab(i);
      });
    });

    this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.track.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.track.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e));
    this.track.addEventListener('lostpointercapture', (e) => this.onPointerUp(e));
  }

  positionIndicator(index, animate = true) {
    const tab = this.tabs[index];
    if (!tab) return;

    if (!animate) {
      this.indicator.style.transition = 'none';
      this.indicator.offsetHeight; // force reflow
    }

    this.indicator.classList.toggle('tab-selector__indicator--dragging', !animate);
    this.track.style.setProperty('--tab-count', String(this.tabs.length));
    this.indicator.style.setProperty('--active-index', String(index));
    this.indicator.style.width = '';
    this.indicator.style.transform = '';

    if (!animate) {
      this.indicator.offsetHeight;
      this.indicator.style.transition = '';
    }

    this.updateCoveredTabs(tab.offsetLeft, tab.offsetWidth);
  }

  selectTab(index) {
    if (index < 0 || index >= this.tabs.length) return;
    const changed = index !== this.activeIndex;

    this.tabs[this.activeIndex].classList.remove('tab-selector__tab--active');
    this.tabs[this.activeIndex].setAttribute('aria-selected', 'false');

    this.activeIndex = index;

    this.tabs[index].classList.add('tab-selector__tab--active');
    this.tabs[index].setAttribute('aria-selected', 'true');

    this.positionIndicator(index, true);

    if (changed) {
      this.dispatchEvent(new CustomEvent('tab-change', {
        detail: {
          index,
          tabId: this.tabs[index].dataset.tabId
        },
        bubbles: true
      }));
    }
  }

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this.isDragging = false;
    this.dragStartX = e.clientX;
    this.indicatorStartX = this.tabs[this.activeIndex].offsetLeft;
    this.pointerIsDown = true;
    this.dragPointerId = e.pointerId;
    this.isTouch = e.pointerType !== 'mouse';

    if (this.isTouch) {
      this.track.setPointerCapture(e.pointerId);
    }

    this.indicator.classList.add('tab-selector__indicator--pressed');
  }

  onPointerMove(e) {
    if (!this.pointerIsDown) return;

    const dx = e.clientX - this.dragStartX;

    if (!this.isDragging && Math.abs(dx) > 4) {
      this.isDragging = true;
      if (!this.isTouch) {
        this.track.setPointerCapture(this.dragPointerId);
      }
      this.indicator.classList.add('tab-selector__indicator--dragging');
      this.tabs[this.activeIndex].classList.remove('tab-selector__tab--active');
    }

    if (!this.isDragging) return;

    const trackWidth = this.track.offsetWidth;
    const indicatorWidth = this.indicator.offsetWidth;
    const minX = 0;
    const maxX = trackWidth - indicatorWidth;
    const newX = Math.max(minX, Math.min(maxX, this.indicatorStartX + dx));

    this.indicator.style.transform = `translateX(${newX}px)`;
    this.updateCoveredTabs(newX, this.indicator.offsetWidth);
  }

  onPointerUp(e) {
    if (!this.pointerIsDown) return;
    this.pointerIsDown = false;

    this.indicator.classList.remove('tab-selector__indicator--pressed');
    this.indicator.classList.remove('tab-selector__indicator--dragging');

    if (this.isDragging) {
      const currentX = this.getCurrentIndicatorX();
      const closest = this.getClosestTab(currentX);
      this.isDragging = false;
      this.selectTab(closest);
    }

    this.isDragging = false;
  }

  getCurrentIndicatorX() {
    const style = getComputedStyle(this.indicator);
    const matrix = new DOMMatrix(style.transform);
    return matrix.m41;
  }

  updateCoveredTabs(indLeft, indWidth) {
    const indRight = indLeft + indWidth;
    this.tabs.forEach(tab => {
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      const overlap = Math.max(0, Math.min(indRight, tabRight) - Math.max(indLeft, tabLeft));
      tab.classList.toggle('tab-selector__tab--covered', overlap > tab.offsetWidth * 0.5);
    });
  }

  getClosestTab(x) {
    let closest = 0;
    let minDist = Infinity;
    const indicatorCenter = x + this.indicator.offsetWidth / 2;

    this.tabs.forEach((tab, i) => {
      const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const dist = Math.abs(indicatorCenter - tabCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }
}

if (!customElements.get('tab-selector')) {
  customElements.define('tab-selector', TabSelector);
}
